import React, {useCallback, useEffect, useState, useRef} from "react";
import package_json from "../package.json";

import {IariError} from "./errors/IariError";

// import {debounce} from "./utils/generalUtils.js";
import {getPagePathEndpoint, noBookLink} from "./utils/iariUtils.js";

import MakeLink from "./components/MakeLink.jsx";
import Dropdown from "./components/Dropdown.jsx";
import Loader from "./components/Loader.jsx";
import PathNameFetch from "./components/PathNameFetch.jsx";
import PageDisplay from "./components/PageDisplay.jsx";

import {IariSources} from "./constants/iariSources.jsx";
import {UrlStatusCheckMethods} from "./constants/checkMethods.jsx";
import {ParseMethods} from "./constants/parseMethods.jsx";

import {ConfigContext} from "./contexts/ConfigContext"
import {ShortcutDefs, envShortcutLists} from "./constants/shortcutDefs.jsx";
import {KNOWN_MEDIA_TYPES} from "./constants/knownMediaTypes.jsx";
import {ACTIONS_IARE} from "./constants/actionsIare.jsx";
import {iareAlert} from "./utils/generalUtils.js";
import {TooltipProvider as MyTooltipProvider} from "./contexts/TooltipContext.jsx";
import {Tooltip as AppTooltip} from "react-tooltip";
import DraggableDiv from "./components/DraggableDiv.jsx";
import NativeDraggableDiv from "./components/NativeDraggableDiv.jsx";  // tooltip for outra-app tooltip
// NB this is temporary - shall be removed when we fix the ScrollFix element to the main App tooltip


export default function App(
    {
        myEnv,
        myPath,
        myRefresh = true,
        myShowShortcuts = false,
        myUseLocalCache = false,
        myCheckMethod,
        myParseMethod,
        myIariSourceId,
        myDebug
    }) {

    const appTitle = "Internet Archive Reference Explorer"

    const [isDebug, setDebug] = useState(myDebug);
    const [isScrollFix, setIsScrollFix] = useState(() => {
        // // // code for test if local exists or npt
        // const saved = localStorage.getItem('isScrollFix');
        // return saved ? JSON.parse(saved) : false;
        return false;
    });

    // these are config values to show/hide certain UI features, available from debug info box
    const [isShowTestFilters, setIsShowTestFilters] = useState(false);
    const [isShowShortcuts, setIsShowShortcuts] = useState(myShowShortcuts);
    // TODO set this based on local storage or cookie value
    const [isShowUseLocalCache, setIsShowUseLocalCache] = useState(
        myEnv === "env-local"
    );

    const [isShowPageInfo, setIsShowPageInfo] = useState(false);

    const [isShowDebugInfo, setIsShowDebugInfo] = useState(false);
    const [isShowDebugComponents, setIsShowDebugComponents] = useState(false);

    const [isShowViewOptions, setIsShowViewOptions] = useState(true);
    const [isShowHamburger, setIsShowHamburger] = useState(false);

    // params settable from from address url
    const [targetPath, setTargetPath] = useState(myPath);
    const [useLocalCache, setUseLocalCache] = useState(myUseLocalCache);
    const [refreshCheck, setRefreshCheck] = useState(myRefresh);
    const [checkMethod, setCheckMethod] = useState(myCheckMethod);
    const [parseMethod, setParseMethod] = useState(myParseMethod);

    // states of page
    const [endpointPath, setEndpointPath] = useState('');
    const [pageData, setPageData] = useState(null);
    const ERROR_MESSAGES = {
        'NETWORK': 'Failed to fetch - IARI service failure: Service down or CORS issue.',
        'NOT_FOUND': '404 Error finding target page.',
        'SERVER': '502 Server problem (no further info available)',
        'IARI': 'IARI failure: ',
        'DEFAULT': 'Unhandled error'
    };
    const [appError, setAppError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // values of screen elements
    const [scrollY, setScrollY] = useState(0);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    // const lowerSectionElementRef = useRef(null);
    const [lowerSectionTopY, setLowerSectionTopY] = useState(0);
    // const [lowerSectionHeight, setLowerSectionHeight] = useState(0);

// DO NOT DELETE YET!// Define CSS styles for logo and add to document

// const styles = {
//     '.header-title-section': {
//         display: 'flex',
//         alignItems: 'center',
//         gap: '.45rem'
//     },
//     '.app-logo': {
//         height: '1.65rem',
//         width: 'auto',
//         marginLeft: '0.27rem',
//     }
// }
// Object.entries(styles).forEach(([selector, rules]) => {
//     const styleEl = document.createElement('style')
//     styleEl.textContent = `${selector} { ${Object.entries(rules).map(([prop, value]) =>
//         `${prop.replace(/[A-Z]/g, m => '-' + m.toLowerCase())}: ${value}`).join(';')} }`
//     document.head.appendChild(styleEl)
// })


    const toggleDebug = () => {
        setDebug(!isDebug);
    }

    const toggleScrollFix = () => {
        // toggles value of isScrollFix, which determines scroll fix mode of screen
        setIsScrollFix(prev => {
            // isScrollFix true allows scrolling without problems screen wide
            // isScrollFix false pegs scrolling to sections, so functionality may crowd
            const newScrollValue = !prev;
            // set style property of about-to-be-new value before setting state value;
            // this ensures the displayed value is in sync with stat-ed value
            document.documentElement.style.setProperty(
                "--iare-ux-body-overflow-y",
                newScrollValue ? "inherit" : "auto"
            );
            localStorage.setItem('isScrollFix', JSON.stringify(newScrollValue));  // so next invocation will remember
            return newScrollValue;
        });

    }

    const handleScroll = () => {
        setScrollY(window.scrollY);
    }

    // // resize components when window size changed
    // const handleResize = useCallback(() => {
    //     setWindowHeight(window.innerHeight);  // for window height display purposes only
    //
    //     if (lowerSectionElementRef.current) {
    //         const rect = lowerSectionElementRef.current.getBoundingClientRect();
    //         setLowerSectionTopY(rect.top); // Get the top Y-coordinate
    //         setLowerSectionHeight(window.innerHeight - rect.top);// Set the new element height
    //     }
    // }, [])


    const myShortcutList = React.useMemo(() => {
        return myEnv === 'env-production'
            ? envShortcutLists.prod
            : myEnv === 'env-staging'
                ? envShortcutLists.stage
                : envShortcutLists.other
    }, [myEnv, envShortcutLists])

    const shortcuts = React.useMemo(() => {
        return myShortcutList
            // output array of objects with label and value props (to send to PathNameFetch)
            ? myShortcutList.map(sKey => {
                return ShortcutDefs[sKey]
                    ? ShortcutDefs[sKey]
                    : {
                        label: "Unknown Shortcut '" + sKey + "'",
                        value: "unknown"
                    }
            }).filter(sc => {
                return sc.value !== "unknown"
            }) // filter out the unknown ones
            : []
    }, [myShortcutList, ShortcutDefs]);


    // add environment tag to body element's class list to enable selective environment styling
    useEffect(() => {
        console.log('APP: useEffect[myEnv]: app name: ' + package_json.name, ', version: ' + package_json.version)
        document.body.classList.add(myEnv);

        // // pull local storage settings
        // const savedScrollFix = localStorage.getItem('isScrollFix');
        // if (savedScrollFix !== null) {
        //     setIsScrollFix(JSON.parse(savedScrollFix));
        // }
    }, [myEnv])

    // add event listener for scroll and resize events
    // useEffect(() => {
    //
    //     const debounceHandleResize = debounce(() => {
    //         handleResize();
    //     }, 200);
    //
    //     // Update the Y-coordinate when the component mounts
    //     handleResize();
    //
    //     // listen for events on window
    //     window.addEventListener('scroll', handleScroll);
    //     window.addEventListener('resize', debounceHandleResize);
    //
    //     // Clean up the event listener on component unmount
    //     return () => {
    //         window.removeEventListener('scroll', handleScroll);
    //         window.removeEventListener('resize', handleResize);
    //     };
    // }, [handleResize]);

    // // when debug shows/hides, readjust onscreen component sizes
    // useEffect(() => {
    //     handleResize()
    // }, [isDebug, handleResize]);


    function getDisplayVersion(pageData, endpointPath) {
        // eslint-disable-next-line
        const regexVersion1 = new RegExp("\/v1\/");
        // eslint-disable-next-line
        const regexVersion2 = new RegExp("\/v2\/");
        // eslint-disable-next-line
        const regexVersion2PDF = new RegExp("\/v2\/statistics\/pdf");

        if (!endpointPath) {
            return "unknown";
        }
        if (regexVersion1.test(endpointPath))
            return "v1"
        else if (regexVersion2.test(endpointPath))
            return "v2"
        else if (regexVersion2PDF.test(endpointPath))
            return "v2PDF"
        else
            return "unknown";
    }

    // mediaType is "pdf", "html", "wiki", or anything else we come up with
    const getMediaType = (path = '') => {
        // set media type based on heuristic:

        if (KNOWN_MEDIA_TYPES.WIKI.regex.test(path))
            return KNOWN_MEDIA_TYPES.WIKI.key

        else if (KNOWN_MEDIA_TYPES.GROK.regex.test(path))
            return KNOWN_MEDIA_TYPES.GROK.key

        else if (KNOWN_MEDIA_TYPES.PDF.regex.test(path))
            return KNOWN_MEDIA_TYPES.PDF.key

        else
            return KNOWN_MEDIA_TYPES.UNKNOWN.key

    };


    // fetch article reference data
    //
    // TODO: account for error conditions, like wrong file format, not found, etc
    //
    const fetchArticleData = useCallback((
        {
            pathName,
            use_local_cache = false,
            refresh = false
        }) => {

        // handle null pathName
        if (!pathName) {
            console.log("APP::fetchArticleData: pathName is null-ish");
            setPageData(null);
            // TODO: use setMyError(null); // ??
            return;
        }

        // mediaType is "pdf", "html", "wiki", or anything else we come up with
        const myMediaType = getMediaType(pathName);
        // TODO: idea: respect a "forceMediaType",
        //  where it can force a media type endpoint, no matter what getMediaType thinks it is.
        //  If so, passes it in to getPagePathEndpoint, where the endpoint is determined
        //  by passed in mediaType rather than mediaType interpolated from pathName.

        console.log("APP: fetchArticleData: mediaType = ", myMediaType)

        const pageEndpoint = getPagePathEndpoint({
            iariSourceId: myIariSourceId,
            path: pathName,
            use_local_cache: use_local_cache,
            mediaType: myMediaType,
            refresh: refresh,
            parseMethod: parseMethod
        })

        console.log("APP::fetchArticleData: endpoint = ", pageEndpoint)
        setEndpointPath(pageEndpoint); // for display purposes only

        // TODO: maybe also always clear pageData, so components get
        //  cleared while waiting?
        // setAppError(null);
        setIsLoading(true);

        // fetch the article data
        fetch(pageEndpoint, {})

            .then(async (res) => {
                const data = await res.json(); // parse JSON even for 500 errors
                if (!res.ok) {
                    /*
                        "data" variable expected to be like:
                        {
                            errors: [
                                {
                                    error: "WikipediaApiFetchError",
                                    details: "Error fetching details"
                                }
                            ]
                         }
                    */
                    const details =
                        data?.errors?.[0]?.details ??
                        data?.error?.details ??
                        null;

                    throw new IariError(details);
                }

                return data;
            })

            .then((data) => {

                // decorate the data with some informative fields upon successful data response
                data.pathName = pathName;
                data.endpoint = pageEndpoint;
                data.iariSource = IariSources[myIariSourceId]?.proxy;
                data.iariVersion = data.iari_version ? data.iari_version : "unknown";
                data.iariParseMethod = parseMethod;
                data.checkStatusMethod = checkMethod;
                data.forceRefresh = refresh;
                data.mediaType = myMediaType; // decorate based on mediaType?
                data.displayVersion = getDisplayVersion(data, pageEndpoint);  // version of pageData - determines display components

                // and set the new pageData state
                setPageData(data);
            })

            .catch((err) => {
                if (err.name === IariError.name) {
                    setAppError(ERROR_MESSAGES.IARI + err.message);
                } else if (err.message === "404") {
                    setAppError(ERROR_MESSAGES.NOT_FOUND);
                } else if (err.message === "502") {
                    setAppError(ERROR_MESSAGES.SERVER);
                } else if (err.name === "TypeError" && err.message === "Failed to fetch") {
                    setAppError(ERROR_MESSAGES.NETWORK);
                } else {
                    setAppError(`${ERROR_MESSAGES.DEFAULT} ${err.name}: ${err.message}`);
                }
                setPageData(null);
            })

            .finally(() => {
                // console.log("fetch finally")
                setIsLoading(false);
            });

    }, [myIariSourceId, parseMethod])


    const handlePathResults = (pathResults) => {
        // callback for PathNameFetch component
        // pathResults[0] = pathName (string)
        // pathResults[1] = checkboxRefresh (boolean)
        // pathResults[2] = checkboxUseLocalCache (boolean)

        refreshPageResults(
            {
                url: pathResults[0],
                refresh: pathResults[1],
                use_local_cache: pathResults[2],
                iari_source: myIariSourceId,
            }
        )

    }

    // set up new url with appropriate "command line args", and refresh page
    const refreshPageResults = (
        {
            url = '',
            cache_data = '',
            use_local_cache = '',
            refresh = false,
            iari_source = IariSources.iari_prod.key
        }) => {

        const newUrl = window.location.protocol + "//"
            + window.location.host + window.location.pathname
            + `?url=${url}`
            + (cache_data ? `&cache_data=${cache_data}` : '')
            + (use_local_cache ? `&use_local_cache=${use_local_cache}` : '')
            + (refresh ? '&refresh=true' : '')
            + (checkMethod ? `&method=${checkMethod}` : '')
            + (myIariSourceId ? `&iari-source=${iari_source}` : '')
            + (parseMethod ? `&parse_method=${parseMethod}` : '')
            + (isDebug ? '&debug=true' : '')

        console.log("refreshPageResults: new url path = ", newUrl)

        // setting the page url forces page to refresh, thus a "new  component render",
        // kicking off the following useEffect
        window.location.href = newUrl;

    }


    // eslint-disable-next-line no-unused-vars
    const handleAction = useCallback( result => {
        const {action, value} = result;
        console.log (`App: handleAction: action=${action}, value=${value}`);

        if (0) {
            // allows for easy addition of "else if"
        }

        else if (action ===
            ACTIONS_IARE.TOGGLE_SHOW_VIEW_OPTIONS.key
        ) {
            // iareAlert("ARF ARF will toggle view options")
            setIsShowViewOptions(prevState => !prevState)
        }

        else {
            console.log(`Action "${action}" not supported.`)
            alert(`Action "${action}" not supported.`)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    // fetch initial article specified on address bar with url param
    useEffect(() => {

        // set these states for debug display, essentially
        setTargetPath(myPath);
        setUseLocalCache(myUseLocalCache)
        setRefreshCheck(myRefresh);

        console.log(`APP: useEffect[myIariSourceId, myPath, myUseLocalCache, myRefresh, fetchArticleData]: calling fetchArticleData: ${myPath}, ${myUseLocalCache}, ${myRefresh}`)

        // and do the fetching for the path specified (pulled from URL address)
        fetchArticleData({
            pathName: myPath,
            use_local_cache: myUseLocalCache,  // debug helper mechanism to get parsed file contents from local cache
            refresh: myRefresh
        })


    }, [myIariSourceId, myPath, myRefresh, myUseLocalCache, fetchArticleData])


    const handleCheckMethodChange = (methodId) => {
        // console.log(`handleStatusMethodChange: new method is: ${methodId}`)
        setCheckMethod(methodId);
    }
    const methodChoices = Object.keys(UrlStatusCheckMethods).filter(f => !["IARI", "IABOT_SEARCHURL"].includes(f)).map(key => {
        return {caption: UrlStatusCheckMethods[key].caption, value: UrlStatusCheckMethods[key].key}
    })
    const methodChoiceSelect = <div className={"choice-wrapper check-method-wrapper"}>
        <Dropdown choices={methodChoices} label={'Check Method:'} onSelect={handleCheckMethodChange}
                  defaultChoice={checkMethod}/>
    </div>

    const handleArticleVersionChange = (articleVersionId) => {
        // console.log(`handleStatusMethodChange: new method is: ${methodId}`)
        setParseMethod(articleVersionId);
    };
    const articleVersionChoices = Object.keys(ParseMethods).map(key => {
        return {caption: ParseMethods[key].caption, value: ParseMethods[key].key}
    })
    const articleVersionChoiceSelect = <div className={"choice-wrapper article-version-wrapper"}>
        <Dropdown choices={articleVersionChoices}
                  label={'Article Parser Version:'}
                  onSelect={handleArticleVersionChange} defaultChoice={parseMethod}/>
    </div>


    const handleIariSourceIdChange = (sourceId) => {
        // console.log(`handleIariSourceChange: new iari source is: ${sourceId}`)
        refreshPageResults({
            url: targetPath,
            cache_data: cacheData,
            use_local_cache: use_local_cache,
            refresh: refreshCheck,
            iari_source: sourceId,
        })
        // setIariSourceId(sourceId);
    };


    const appServerInfo = (myEnv !== 'env-production')  // TODO implement IareEnvironments
        ? `App Server: ${myEnv !== 'env-staging' ? ' LOCAL ' : ' STAGING '}`
        : ''  // do not show myEnv in production environment


    const iariServerInfo = `IARI Server: ${IariSources[myIariSourceId]?.caption}`

    const iariVersionInfo = `IARI Version: ${pageData?.iariVersion ?? 'unknown'}`

    const svgLockIconOpen = <svg className={"svg-icon-box"} xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                 viewBox="0 0 16 16"
                                 fill="currentColor"
                                 >
        <path d="M11 1a2 2 0 0 0-2 2v4a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h5V3a3 3 0 0 1 6 0v4a.5.5 0 0 1-1 0V3a2 2 0 0 0-2-2z"/>
    </svg>
    // const svgLockIconOpen = "open"
    const svgLockIconClosed = <svg className={"svg-icon-box"} xmlns="http://www.w3.org/2000/svg"
                                   width="16" height="16"
                                   viewBox="0 0 16 16"
                                   fill="currentColor"
                                   >
        <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
    </svg>
    // const svgLockIconClosed = "closed"


    const buttonScrollFix = <div>
        {/*// For small screens, turning on "scrollfix" allows more elements to be scrolled. */}
        {/*// Otherwise, navigation elements on a fixed screen take up so much room there is */}
        {/*// not much left for scrolling data!                                              */}

        {/*// NB this is an experimental feature so far... */}

        <div>Scroll Fix (maybe): <span className={"lock-icon"}
                           onClick={toggleScrollFix}
                           data-tooltip-id="app-tooltip-id"
                           data-tooltip-content={isScrollFix
                               ? "Currently in \"fixed\" mode - Click to return to default"
                               : "Currently in default mode - Click to enter \"fixed\" mode."}>
            {isScrollFix
                ? svgLockIconOpen
                : svgLockIconClosed
            }
            </span></div>
    </div>


    // const buttonShowDebugOn = "+"
    // const buttonShowDebugOff = <>&#8212;</>
    const buttonShowDebugOn = "Show Debug"
    const buttonShowDebugOff = "Hide Debug"

    const buttonShowDebug = (myEnv !== 'env-production') &&
        <button className={"utility-button debug-button small-button"}
                style={{marginLeft:"0rem", marginTop: ".25rem", marginBottom: ".25rem"}}
                onClick={toggleDebug}>{
            isDebug
                // ? <>&#8212;</>
                // : "+"  // dash (&#8212;) and plus sign
                ? buttonShowDebugOff
                : buttonShowDebugOn
            // up triangle: <>&#9650;</>
            // dn triangle: <>&#9660;</>
        }</button>

    const togglePageInfo = <div className={"toggle-prop-text"}
                onClick={() => {
                    setIsShowPageInfo(prevState => {
                        return !prevState
                    })
                }}
                >
        {isShowPageInfo ? "Hide PageInfo" : "Show PageInfo"}
    </div>

    useEffect(() => {
        const setVal = isShowPageInfo ? 'inherit' : 'none';
        document.querySelector(
            '.page-info'
        )?.style.setProperty('display', setVal)
    }, [isShowPageInfo])

    const debugButtonFilters = <button // this is the 'show urls list' button
        className={"utility-button debug-button"}
        onClick={() => {
            setIsShowTestFilters(prevState => !prevState)
        }
        }>{isShowTestFilters ? "Hide" : "Show"} Test Filters</button>


    const debugButtonShortcuts = <button // this is the 'show shortcuts' button
        className={"utility-button debug-button"}
        onClick={() => {
            setIsShowShortcuts(prevState => !prevState)
        }
        }>{isShowShortcuts ? "Hide" : "Show"} Shortcuts</button>


    const debugButtonViewTypes = <button // this is the 'show view options' button
        className={"utility-button debug-button"}
        onClick={() => {
            setIsShowViewOptions(prevState => !prevState)
        }
        }>{isShowViewOptions ? "Hide" : "Show"} View Options</button>

    const debugButtonDetails = <button // this is the 'show New Features' button
        className={"utility-button debug-button"}
        onClick={() => {
            setIsShowDebugInfo(prevState => !prevState)
        }
        }>{isShowDebugInfo ? "Hide" : "Show"} Debug Details</button>

    const debugButtonComponents = <button // this is the 'show New Features' button
        className={"utility-button debug-button"}
        onClick={() => {
            setIsShowDebugComponents(prevState => !prevState)
        }
        }>{isShowDebugComponents ? "Hide" : "Show"} Debug Components</button>

    const debugButtons = <>
        {debugButtonViewTypes}
        &nbsp;
        {debugButtonDetails}
        &nbsp;
        {debugButtonComponents}
        &nbsp;
        {debugButtonFilters}
        &nbsp;
        {debugButtonShortcuts}
    </>

    const iariChoices = Object.keys(IariSources)
        .filter(key => {
            return myEnv === 'env-staging'
                ? !(key === "iari_local" || key === "iari")  // filter out iari_local and iari on Staging
                : true
        })
        .map(key => {
            return {caption: IariSources[key].caption, value: IariSources[key].key}
        })

    const iariChoiceSelect = <div className={"choice-wrapper iari-source-wrapper"}>
        <Dropdown choices={iariChoices} label={'Iari Source:'} onSelect={handleIariSourceIdChange}
                  defaultChoice={myIariSourceId}/>
    </div>

    const versionInfo = package_json.version

    const debugDisplay = <div className={"debug-section " + (isDebug ? "debug-on" : "debug-off")}>
        {/*<div style={{marginBottom:".5rem"}}*/}
        {/*>{iariChoiceSelect} {methodChoiceSelect} {articleVersionChoiceSelect}</div>*/}
        <div>{iariChoiceSelect} {methodChoiceSelect} {articleVersionChoiceSelect}</div>
        <p><span className={'label'}>Environment:</span> {myEnv} <span
            className={'lolite'}>(host: {window.location.host})</span></p>
        <p><span className={'label'}>IARE Version:</span> {versionInfo}</p>
        <p><span className={'label'}>IARI Source:</span> {myIariSourceId} <span
            className={'lolite'}>({IariSources[myIariSourceId]?.proxy})</span></p>
        <p><span className={'label'}>IARI Version:</span> {pageData?.iariVersion
            ? pageData.iariVersion
            : "unknown"}</p>
        <p><span className={'label'}>Parse Method:</span> {ParseMethods[parseMethod].caption}</p>
        <p><span className={'label'}>Check Method:</span> {checkMethod} <span
            className={'lolite'}>({UrlStatusCheckMethods[checkMethod].caption})</span></p>
        <p><span className={'label'}>Target URL from query param:</span> {myPath}</p>
        <p><span className={'label'}>Force Refresh:</span> {refreshCheck ? "TRUE" : "false"}</p>
        <p><span className={'label'}>Use Local Cache:</span> {useLocalCache ? "TRUE" : "false"}</p>
        <p><span className={'label'}>Show View Options:</span> {isShowViewOptions ? "TRUE" : "false"}</p>
        <p><span className={'label'}>pathName:</span> <MakeLink href={targetPath}/></p>
        <p><span className={'label'}>endpointPath:</span> <MakeLink href={endpointPath}/></p>
        <div>{debugButtons}</div>
    </div>

    // set config for config context
    const config = {
        environment: myEnv,
        iariSource: IariSources[myIariSourceId]?.proxy,
        iariSourceId: myIariSourceId,
        wikiBaseUrl: "https://en.wikipedia.org/wiki/",
        urlStatusMethod: checkMethod,
        articleVersion: myParseMethod,
        isDebug: !!isDebug,
        isShowTestFilters: isShowTestFilters,
        isShowShortcuts: isShowShortcuts,
        isShowDebugInfo: isShowDebugInfo,
        isShowDebugComponents: isShowDebugComponents,
        isShowViewOptions: isShowViewOptions,
    }

    console.log(`APP: Rendering App component:`, JSON.stringify({
        path: targetPath,
        refreshCheck: refreshCheck,
        statusMethod: checkMethod,
        iari_source: myIariSourceId,
        // config: config,
    }))

    const defaultIfEmpty = "https://en.wikipedia.org/wiki/"
    // TODO candidate for language strings


    useEffect(() => {
        // setMyError("Fake Error here!")
    }, [])


    const tooltipApp = <AppTooltip id="app-tooltip-id"
                                   float={true}
                                   clickable
                                   closeOnEsc={true}
                                   delayShow={420}
                                   variant={"info"}
                                   noArrow={true}
                                   offset={5}
                                   className={"app-tooltip"}
                                   style={{zIndex: 999, backgroundColor: "rgba(0,0,255,0.8)"}}
    >
        <div className={"app-tooltip-content"}>
            <div className={"app-tooltip-title"}>
                <div className={"app-tooltip-title-text"}>
                    <span className={"app-tooltip-title-text-main"}>App Tooltip</span>
                    <span className={"app-tooltip-title-text-sub"}>
                        (click to close)
                    </span>
                </div>
            </div>
            <div className={"app-tooltip-body"}>

            </div>
        </div>
    </AppTooltip>

    console.log(`appError: ${appError}`)

    const hamburgerMenu = <div className="iare-hamburger-wrapper">
        <button onClick={() => setIsShowHamburger(prevState => !prevState)}>
            Menu
        </button>

        {isShowHamburger && (
            <div className={"iare-hamburger-menu"}
                            // popover={"auto"}
                            // popover-id={""}
            >
                <div className={"menu-header"}>
                    <div>IARE version {versionInfo}</div>
                </div>

                <div>
                    <div>{appServerInfo}</div>
                    <div>{iariServerInfo}</div>
                    <div>{iariVersionInfo}</div>
                    <div>{togglePageInfo}</div>
                    <div>{buttonScrollFix}</div>
                    <div>{buttonShowDebug}</div>
                </div>
            </div>
        )}

    </div>

    
    return <MyTooltipProvider>

        {/* This is a Static Debug Display, a floatable debug
            window that starts out at the top, right of the screen.
            This only shows up when running in local environment */}
        {/*{(myEnv === 'env-local') && <NativeDraggableDiv />}*/}

        <ConfigContext.Provider value={config}>

            {/*<div className="iare-view">*/}

            <div className={"iare-ux-container main-container"}>

                <div className={"iare-ux-header main-header"}>

                    <div className={"main-header-contents"}>

                        <div className="header-title-section">
                            <img src={"logo192.png"} alt="App Logo" className="app-logo"/>
                            <h1 className={"app-title"}>{appTitle}</h1>
                        </div>

                        <div><span style={{marginRight: ".75rem"}}>v {versionInfo}</span>{hamburgerMenu}</div>
                        
                    </div>

                    {debugDisplay}

                </div>

                <div className={"iare-ux-body main-body"}>

                    <div className={"iare-ux-container page-container"}>

                        <div className={"iare-ux-header page-header"}>

                            <PathNameFetch pathInitial={targetPath?.length > 0 ? targetPath : defaultIfEmpty}
                                           className={"iare-path-fetch"}
                                           initialBooleanRefresh={refreshCheck}
                                           initialBooleanUseLocalCache={useLocalCache}
                                           placeholder={"Enter a Wikipedia article or PDF url here"}
                                           shortcuts={shortcuts}
                                           handlePathResults={handlePathResults}
                                           options={{
                                               showShortcuts: isShowShortcuts,
                                               showUseLocalCache: isShowUseLocalCache,
                                           }}

                            />

                            {appError
                                ? <div className={appError ? "error-display" : "error-display-none"}>
                                    {appError}
                                </div>
                                : ""
                            }

                        </div>

                        <div className={"iare-ux-body page-body"}>

                            {/*<div className={"test-contents"} >*/}
                            {/*    <h2>test-contents</h2>*/}
                            {/*    {Array.from({length: 20}, (_, i) => {return <p>Body Here!</p>})}*/}
                            {/*</div>*/}
                            {isLoading
                                ? <Loader message={"Analyzing Page References..."}/>
                                : <>
                                    { /* component is re-rendered when pageData changes, which is
                             only once per URL invocation, really */}
                                    <PageDisplay pageData={pageData} onAction = {handleAction} />
                                    { /* TODO: pass in an error callback here? */}
                                </>
                            }
                        </div>

                    </div>

                </div>

            </div>

            {/*</div> <!-- end iare-view -->*/}

            {tooltipApp /* shall be removed when scrollFix tooltip implements global tooltip */}

        </ConfigContext.Provider>

    </MyTooltipProvider>
}
